import { noop } from 'lodash';
import { FC, useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Updater, useImmer } from 'use-immer';

import { ExerciseSkill } from '@gmm/sdk-voodoo/shared';
import { useLatestRef } from '@gmm/ui';
import { createNamedContext } from '~/lib/createNamedContext';
import { useExercise } from '~/lib/hooks/useExercise';
import { getEmptyArray } from '~/lib/type-utils';
import {
  CREATE_ASSIGNMENT_PATH,
  CREATE_EXAM_PATH,
} from '~/work/skillDiscovery/utils';
import { useSWRConfig } from 'swr';
import axios from 'axios';
import { destroy } from '@gmm/sdk-voodoo/examProblems';
import { useExamTemplateStore } from '~/work/teacherWork/[exerciseId]/stores/examTemplateStore';
import { useDuplicationStore } from '~/work/skillDiscovery/stores';
import { useExerciseStatus } from '~/work/teacherWork/components/breadcrumbs/hooks';

const AddedSkillsContext = createNamedContext('AddSkillsContext', {
  // unique skill types added to the exercise
  skills: getEmptyArray<ExerciseSkill[]>(),
  // instances to add to locked exam
  instances: getEmptyArray<ExerciseSkill[]>(),
  aggregate: false,
});

export const SetSkillsContext = createNamedContext<
  Updater<{
    skills: ExerciseSkill[];
    instances: ExerciseSkill[];
    aggregate: boolean;
  }>
>('SetSkillsContext', noop);

export const AddSkillsProvider: FC = ({ children }) => {
  const [state, setState] = useImmer<{
    skills: ExerciseSkill[];
    instances: ExerciseSkill[];
    aggregate: boolean;
  }>(() => ({
    skills: [],
    instances: [],
    aggregate: false,
  }));

  return (
    <SetSkillsContext.Provider value={setState}>
      <AddedSkillsContext.Provider value={state}>
        {children}
      </AddedSkillsContext.Provider>
    </SetSkillsContext.Provider>
  );
};

interface UseAddSkills {
  addSkills: (
    skills: ExerciseSkill[],
    to?: string,
    aggregate?: boolean,
  ) => void;
  createAssignmentWithSkills: (skills: ExerciseSkill[]) => void;
  createExamTemplateWithSkills: (skills: ExerciseSkill[]) => void;
  isSkillAdded: (skillType: string) => boolean;
  resetSkills: () => void;
  resetSkillsAndDeleteDraftLockedExamProblems: (
    triggeredByClickingSave?: boolean,
  ) => void;
}

export const useAddSkills = (): UseAddSkills => {
  const history = useHistory();
  const setState = useContext(SetSkillsContext);
  const { skills } = useContext(AddedSkillsContext);
  const { data: exercise } = useExercise({ readOnly: true });
  const exerciseStatus = useExerciseStatus();
  const clearExamTemplateProblems = useExamTemplateStore(
    state => state.clearLockedProblems,
  );
  const cancelOrSaveDuplicatedFromSkillDiscovery = useDuplicationStore(
    state => state.cancelOrSaveDuplicatedFromSkillDiscovery,
  );

  const { cache, mutate } = useSWRConfig();

  const isSkillAdded = (skillType: string): boolean =>
    [...(exercise?.skills ?? []), ...skills].some(
      ({ type }) => type === skillType,
    );

  // After this function alters 'local' (context-managed) skills, it will
  // trigger the useEffect in hook useSkillsEffect (bottom of this file),
  // which in turn has a callback from component managing form fields
  // (ex: ExamTemplateFields) to merge the skills into Formik state.
  const addSkills = (
    skills: ExerciseSkill[],
    to = CREATE_ASSIGNMENT_PATH,
    aggregate = false,
  ): void => {
    if (!skills.length) return;

    if (!exerciseStatus.isExerciseOpen) {
      const { pathname } = history.location;
      const basePath = pathname.startsWith('/work') ? pathname : '/work';
      // 'push' opens the target create work route. It's like clicking the plus button and choosing assignment or exam
      history.push(`${basePath}${to}`);
    }

    setState(draft => {
      skills.forEach(skill => {
        if (!isSkillAdded(skill.type)) draft.skills.push(skill);
        if (aggregate) draft.instances.push(skill);
      });
      draft.aggregate = aggregate;
    });
  };

  const resetHelper = (): void => {
    setState(draft => {
      draft.skills = getEmptyArray<ExerciseSkill[]>();

      draft.aggregate = false;
    });

    clearExamTemplateProblems();
  };

  const resetSkills = useCallback(async (): Promise<void> => {
    resetHelper();
  }, [setState, clearExamTemplateProblems]);

  const resetSkillsAndDeleteDraftLockedExamProblems = useCallback(
    async (triggeredByClickingSave?: boolean): Promise<void> => {
      resetHelper();

      if (!exercise?.id || exercise.type !== 'ExamTemplate') return;

      cancelOrSaveDuplicatedFromSkillDiscovery(triggeredByClickingSave);

      // tell server to delete draft locked exam problems
      const config = destroy(exercise.id.toString());
      await axios.request(config);
    },
    [setState, clearExamTemplateProblems, exercise, mutate, cache],
  );

  useEffect(() => {
    return history.listen(resetSkills);
  }, [exercise, history, resetSkills]);

  return {
    addSkills,
    createAssignmentWithSkills: skills => addSkills(skills),
    createExamTemplateWithSkills: skills => addSkills(skills, CREATE_EXAM_PATH),
    isSkillAdded,
    resetSkills,
    resetSkillsAndDeleteDraftLockedExamProblems,
  };
};

// ExamTemplateFields and AssignmentFields send a 'callback' here
// to merge added skills into Formik state.
export const useSkillsEffect = (
  callback: (newSkills: ExerciseSkill[], aggregate?: boolean) => void,
): void => {
  const { skills, instances, aggregate } = useContext(AddedSkillsContext);
  const setState = useContext(SetSkillsContext);
  const callbackRef = useLatestRef(callback);

  useEffect(() => {
    if (aggregate) {
      callbackRef.current(instances, true);
      //Drain the 'local' (context-managed) instances after the callback has processed them.
      setState(draft => {
        if (draft.instances.length > 0) draft.instances = [];
      });
    } else if (skills.length) {
      callbackRef.current(skills, false);
    }
  }, [callbackRef, skills, instances, setState, aggregate]);
};
